import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_BASE } from '../lib/config';
const API = `${API_BASE}`;

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  // 自動スライド機能
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === banners.length - 1 ? 0 : prevIndex + 1
        );
      }, 4000); // 4秒間隔

      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/carousel/banners`);
      setBanners(response.data);
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError('バナーの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleBannerClick = async (banner) => {
    try {
      // クリック数をトラッキング
      await axios.post(`${API}/banners/${banner.id}/click`);

      // リンク先に遷移
      if (banner.link_url) {
        if (banner.link_url.startsWith('http')) {
          window.open(banner.link_url, '_blank');
        } else {
          window.location.href = banner.link_url;
        }
      }
    } catch (err) {
      console.error('Error tracking click:', err);
      // エラーが発生してもリンク先には遷移する
      if (banner.link_url) {
        if (banner.link_url.startsWith('http')) {
          window.open(banner.link_url, '_blank');
        } else {
          window.location.href = banner.link_url;
        }
      }
    }
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? banners.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === banners.length - 1 ? 0 : currentIndex + 1);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="w-full h-48 bg-gray-200 rounded-lg animate-pulse flex items-center justify-center">
        <div className="text-gray-500">広告を読み込み中...</div>
      </div>
    );
  }

  if (error || banners.length === 0) {
    return null; // エラーまたはバナーがない場合は表示しない
  }

  return (
    <div className="relative w-full rounded-lg overflow-hidden shadow-lg bg-gray-100" style={{ aspectRatio: '3/1' }}>
      {/* メインバナー表示エリア */}
      <div className="relative w-full h-full">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <div
              className="w-full h-full cursor-pointer relative"
              onClick={() => handleBannerClick(banner)}
            >
              <img
                src={banner.image_url}
                alt={banner.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDQwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOUI5QjlCIiBmb250LXNpemU9IjE2Ij7nmbrlkYrjgYzjgZPjgb7jgZvjgpPjgpI8L3RleHQ+Cjwvc3ZnPgo=';
                }}
              />

              {/* バナータイトルとオーバーレイ */}
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end">
                <div className="p-4 text-white w-full">
                  <h3 className="text-lg md:text-xl font-bold mb-1">{banner.title}</h3>
                  {banner.description && (
                    <p className="text-sm text-gray-200 line-clamp-2">{banner.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 左右の矢印ボタン */}
      {banners.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </>
      )}

      {/* インジケーター（ドット） */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${index === currentIndex
                  ? 'bg-white scale-110'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                }`}
            />
          ))}
        </div>
      )}

      {/* スマートフォン用のスワイプヒント */}
      <div className="md:hidden absolute bottom-2 right-2 text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
        スワイプで切り替え
      </div>
    </div>
  );
};

export default BannerCarousel;